import './settings-leaf.css';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { Alert, Checkbox, LoadingBlock, TextInput } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';
import { SMS_COMPANY_DAILY_LIMIT_MAX, SMS_SENDER_LABEL_MAX } from '@shared/constants/index.ts';
import { useAppConfig } from '../turnstile.tsx';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * The confirmation request and reminder that go out ahead of each visit, and
 * whether the same two also go out by text. The lead times and the text
 * switches save together because they are one decision — how this company
 * nags — while the Twilio account that makes texting possible at all is its
 * own card under Communication.
 */

const snapshot = (company: Company) => ({
  appointmentConfirmLeadHours: company.appointmentConfirmLeadHours,
  appointmentReminderLeadHours: company.appointmentReminderLeadHours,
  smsRemindersEnabled: company.smsRemindersEnabled,
  smsSenderLabel: company.smsSenderLabel,
  smsDailyLimit: company.smsDailyLimit,
  missedClockInAlertMinutes: company.missedClockInAlertMinutes,
  requireClockInToComplete: company.requireClockInToComplete ?? false,
});

const toPayload = (draft: ReturnType<typeof snapshot>) => ({
  appointmentConfirmLeadHours: draft.appointmentConfirmLeadHours,
  appointmentReminderLeadHours: draft.appointmentReminderLeadHours,
  smsRemindersEnabled: draft.smsRemindersEnabled,
  smsSenderLabel: draft.smsSenderLabel ?? '',
  smsDailyLimit: draft.smsDailyLimit,
  missedClockInAlertMinutes: draft.missedClockInAlertMinutes,
  requireClockInToComplete: draft.requireClockInToComplete,
});

/** What ticking the alert box starts from — half an hour is a crew that is late, not parking. */
const DEFAULT_MISSED_CLOCK_IN_MINUTES = 30;

export default function RemindersCard() {
  const { t } = useTranslation();
  const { company } = useSession();
  const config = useAppConfig();
  const form = useCompanyCardForm(snapshot, toPayload);

  // Whether a text could go out for this company at all: the deployment's
  // Twilio account, or one connected in the messaging card further down.
  const smsSendable = company?.smsConfigured ?? false;

  const draft = form.draft;
  if (!draft) return null;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="reminders-heading">
      <div className="work-section-heading">
        <h2 id="reminders-heading" className="mb-4 text-lg font-semibold">
          {t('settings.remindersHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.remindersDescription')}</p>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <NumberInput
          label={t('settings.confirmLeadHours')}
          min={1}
          max={336}
          value={draft.appointmentConfirmLeadHours}
          disabled={form.readOnly}
          error={form.fieldErrors.appointmentConfirmLeadHours}
          onValueChange={(hours) => form.set('appointmentConfirmLeadHours', hours ?? 0)}
        />
        <NumberInput
          label={t('settings.reminderLeadHours')}
          min={1}
          max={336}
          value={draft.appointmentReminderLeadHours}
          disabled={form.readOnly}
          error={form.fieldErrors.appointmentReminderLeadHours}
          onValueChange={(hours) => form.set('appointmentReminderLeadHours', hours ?? 0)}
        />
      </div>

      <h3 className="mt-6 mb-2 border-t border-[color:var(--color-line)] pt-6 text-base font-semibold">
        {t('settings.textRemindersHeading')}
      </h3>

      {/*
        Fails closed while the answer is unknown. Offering the toggle before
        the deployment config has loaded would let an owner switch text
        reminders on, save, and be told nothing — the server would refuse it,
        or worse accept a setting that can never send anything.
      */}
      {!config.data ? (
        <LoadingBlock label={t('settings.checkingSms')} />
      ) : !smsSendable ? (
        <Alert tone="info">
          {t(company?.managedSmsAvailable === false ? 'settings.smsNotSetUpOwnOnly' : 'settings.smsNotSetUp')}
        </Alert>
      ) : (
        <>
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.smsDescription')}</p>

          <Checkbox
            label={t('settings.sendSmsReminders')}
            hint={t('settings.sendSmsRemindersHint')}
            checked={draft.smsRemindersEnabled}
            disabled={form.readOnly}
            onChange={(event) => form.set('smsRemindersEnabled', event.target.checked)}
          />

          {draft.smsRemindersEnabled ? (
            <div className="grid gap-x-4 sm:grid-cols-2">
              <TextInput
                label={t('settings.smsSenderName')}
                hint={t('settings.smsSenderNameHint', { max: SMS_SENDER_LABEL_MAX })}
                maxLength={SMS_SENDER_LABEL_MAX}
                value={draft.smsSenderLabel ?? ''}
                disabled={form.readOnly}
                error={form.fieldErrors.smsSenderLabel}
                onChange={(event) => form.set('smsSenderLabel', event.target.value)}
              />
              <NumberInput
                label={t('settings.smsDailyLimit')}
                min={1}
                max={SMS_COMPANY_DAILY_LIMIT_MAX}
                hint={t('settings.smsDailyLimitHint')}
                value={draft.smsDailyLimit}
                disabled={form.readOnly}
                error={form.fieldErrors.smsDailyLimit}
                onValueChange={(limit) => form.set('smsDailyLimit', limit ?? 0)}
              />
            </div>
          ) : null}
        </>
      )}

      {/*
        The missed clock-in alert (time clock, commercial mode). Scheduling-ish
        like everything else on this card: it fires off the same cron the
        reminders do, minutes after a visit starts with nobody clocked in.
        Null = off, which is every company's default.
      */}
      <h3 className="mt-6 mb-2 border-t border-[color:var(--color-line)] pt-6 text-base font-semibold">
        {t('settings.timeClockHeading')}
      </h3>
      <Checkbox
        label={t('settings.missedClockInLabel')}
        hint={t('settings.missedClockInHint')}
        checked={draft.missedClockInAlertMinutes !== null}
        disabled={form.readOnly}
        onChange={(event) =>
          form.set('missedClockInAlertMinutes', event.target.checked ? DEFAULT_MISSED_CLOCK_IN_MINUTES : null)
        }
      />
      {draft.missedClockInAlertMinutes !== null ? (
        <div className="grid gap-x-4 sm:grid-cols-2">
          <NumberInput
            label={t('settings.missedClockInMinutesLabel')}
            min={5}
            max={240}
            hint={t('settings.missedClockInMinutesHint')}
            value={draft.missedClockInAlertMinutes}
            disabled={form.readOnly}
            error={form.fieldErrors.missedClockInAlertMinutes}
            onValueChange={(minutes) =>
              form.set('missedClockInAlertMinutes', minutes ?? DEFAULT_MISSED_CLOCK_IN_MINUTES)
            }
          />
        </div>
      ) : null}

      {company?.requireClockInToComplete !== undefined ? (
        <Checkbox
          label={t('settings.requireClockInLabel')}
          hint={t('settings.requireClockInHint')}
          checked={draft.requireClockInToComplete}
          disabled={form.readOnly}
          onChange={(event) => form.set('requireClockInToComplete', event.target.checked)}
        />
      ) : null}
      <CardSaveFooter form={form} saveLabel={t('settings.saveReminders')} />
    </section>
  );
}
